import React, { useState, useEffect } from 'react';
import { LightgalleryProvider, LightgalleryItem } from 'react-lightgallery';
import 'lightgallery.js/dist/css/lightgallery.css';
import { useTranslation } from 'react-i18next';
import { makeStyles, tokens, Spinner } from '@fluentui/react-components';
import { Heading, Container, EmptyState, Breadcrumb } from '../theme/primitives';
import { iconMap } from '../theme/icons';
import { API_BASE } from '../config.js';
import { stonePalette } from '../theme/brand';

const ErrorIcon = iconMap.error;
const GalleryIcon = iconMap.photo_library;

// Šios reikšmės (CATEGORY_ORDER, "Kita" fallback) yra REALIOS DB `type`/`title`
// laukų reikšmės (žr. AdminGallery.jsx) — palyginimas su DB duomenimis, ne UI
// tekstas, tad NEverčiama (žr. shop/ProjectGallery.jsx 'Galerija' filtro tą
// pačią pastabą). Grupių ANTRAŠTĖS puslapyje lieka tokios, kokias administratorius
// įvedė DB — tai vartotojo turinys, ne statinis UI vertimas.
const CATEGORY_ORDER = [
  'Spintos varstomomis durimis',
  'Spintos stumdomomis durimis',
  'Komodos',
  'Lentynos',
  'TV Spintelės',
  'Spintelės',
];

const escapeHtml = (str) =>
  String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const useStyles = makeStyles({
  // Suvienodinta su /baldai/:categoryid antraštės (headerInner) paddingu —
  // anksčiau čia buvo hardcoded 2.5rem, pastebimai didesnis už tą patį
  // token'ą naudojantį FurnitureList.jsx headerInner (žr. /admin/bugs "Per
  // dideli atstumai, stilius turi buti toks pat kaip /baldai/:categoryid").
  titleBar: {
    paddingTop: tokens.spacingVerticalXL, paddingBottom: tokens.spacingVerticalXL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // <Heading as="h1"> renderina tikrą <h1>, kuris be šio nulinimo paveldi
  // naršyklės numatytąjį h1 margin-block (~did paddingas prieš/po) — kitos
  // panašios antraštės (pvz. FurnitureList.jsx) šį margin jau nulina savo
  // pačių title stiliuje (žr. /admin/bugs "sumazinti sita tarpa").
  title: { margin: 0 },
  gallerySection: {
    backgroundColor: stonePalette[50],
    paddingTop: '4rem', paddingBottom: '4rem',
  },
  // Paddingas ant Container'io (ne išorinės sekcijos) — kitaip susidaro
  // dvigubas centravimas ir turinys siauresnis nei header'is (žr. Duk.jsx
  // faqContainer komentarą ir /admin/bugs "susiaurinti galerijos puslapio
  // turini").
  pageContainer: {
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  loadingWrap: { display: 'flex', justifyContent: 'center', paddingTop: '6rem', paddingBottom: '6rem' },
  groups: { display: 'flex', flexDirection: 'column', gap: '3.5rem' },
  groupTitle: {
    fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalL, paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, marginTop: 0,
  },
  groupCount: { marginLeft: tokens.spacingHorizontalXS, fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightRegular, color: tokens.colorNeutralForeground3 },
  masonry: { columns: '4 150px', gap: '8px' },
  itemBox: {
    overflow: 'hidden', borderRadius: tokens.borderRadiusMedium, cursor: 'pointer',
    marginBottom: tokens.spacingVerticalXS, display: 'block', breakInside: 'avoid',
    ':hover img': { transform: 'scale(1.05)' },
  },
  itemImg: {
    width: '100%', display: 'block', objectFit: 'cover',
    transitionProperty: 'transform', transitionDuration: tokens.durationSlow,
  },
});

function Gallery() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const s = useStyles();

  useEffect(() => { document.title = `${t('gallery.title')} | Fitit`; }, [t]);

  useEffect(() => {
    fetch(`${API_BASE}/gallery`)
      .then(res => res.json())
      .then(data => {
        const all = Array.isArray(data) ? data.filter(item => item.type === 'Galerija' || !item.type) : [];

        const grouped = {};
        all.forEach(item => {
          const key = item.title || t('gallery.other');
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(item);
        });

        const sorted = Object.entries(grouped).sort(([a], [b]) => {
          const ai = CATEGORY_ORDER.indexOf(a);
          const bi = CATEGORY_ORDER.indexOf(b);
          if (ai === -1 && bi === -1) return 0;
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        });

        setGroups(sorted);
      })
      .catch((err) => {
        console.error('Nepavyko įkelti galerijos:', err);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [t]);

  const totalCount = groups.reduce((sum, [, items]) => sum + items.length, 0);

  return (
    <div>

      {/* Page Title */}
      <div className={s.titleBar}>
        <Container size="xl" className={s.pageContainer}>
          <Breadcrumb items={[{ label: t('furniture_list.breadcrumb_home'), to: '/' }, { label: t('gallery.title') }]} />
          <Heading as="h1" variant="h1" className={s.title}>{t('gallery.title')}</Heading>
        </Container>
      </div>

      {/* Gallery */}
      <section className={s.gallerySection}>
        <Container size="xl" className={s.pageContainer}>
          {loading ? (
            <div className={s.loadingWrap}>
              <Spinner size="large" />
            </div>
          ) : loadError ? (
            <EmptyState icon={<ErrorIcon fontSize={32} />} title={t('gallery.load_error')} />
          ) : totalCount === 0 ? (
            <EmptyState icon={<GalleryIcon fontSize={32} />} title={t('gallery.empty')} />
          ) : (
            <LightgalleryProvider>
              <div className={s.groups}>
                {groups.map(([category, items]) => (
                  <div key={category}>
                    <h2 className={s.groupTitle}>
                      {category}
                      <span className={s.groupCount}>({items.length})</span>
                    </h2>
                    <div className={s.masonry}>
                      {items.map(item => (
                        <LightgalleryItem
                          key={item._id}
                          src={item.imageUrl}
                          subHtml={`<h4>${escapeHtml(item.title)}</h4>`}
                          group={category}
                        >
                          <div className={s.itemBox}>
                            <img
                              src={item.imageUrl}
                              alt={item.title || t('gallery.photo_alt')}
                              loading="lazy"
                              className={s.itemImg}
                            />
                          </div>
                        </LightgalleryItem>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </LightgalleryProvider>
          )}
        </Container>
      </section>

    </div>
  );
}

export default Gallery;
